import { supabase } from './supabase';
import type {
  XCloudServer, XCloudSite, XCloudSiteStatus, XCloudSSL, XCloudDomain,
  XCloudBackup, XCloudMonitoring, XCloudDatabase, XCloudCronJob,
  XCloudPhpVersion, XCloudSudoUser, XCloudTask, XCloudDeploymentLog,
  XCloudGit, XCloudSsh, XCloudBlueprint, XCloudListResponse, XCloudDetailResponse,
} from '@/types/xcloud';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

async function proxyRequest<T>(
  workspaceId: string,
  path: string,
  method: string = 'GET',
  body?: unknown,
  force: boolean = false,
  page?: number
): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');

  const url = new URL(`${SUPABASE_URL}/functions/v1/xcloud-proxy`);
  url.searchParams.set('workspace_id', workspaceId);
  url.searchParams.set('path', path);
  if (force) url.searchParams.set('force', 'true');
  if (page && page > 1) url.searchParams.set('page', String(page));

  const res = await fetch(url.toString(), {
    method,
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
      Apikey: SUPABASE_ANON_KEY,
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const responseText = await res.text();
  let data: any;
  try {
    data = JSON.parse(responseText);
  } catch {
    data = { error: responseText };
  }

  if (!res.ok) {
    let message = data.error || data.message || `xCloud API error: ${res.status}`;
    
    // Append actual status and details if available from the proxy
    if (data.status && data.status !== res.status) {
      message += ` (HTTP ${data.status})`;
    } else if (res.status !== 200) {
      message += ` (HTTP ${res.status})`;
    }
    
    if (data.details) {
      if (typeof data.details === 'string') {
        message += `: ${data.details}`;
      } else if (data.details.message) {
        message += `: ${data.details.message}`;
      } else if (data.details.error) {
        message += `: ${data.details.error}`;
      }
    }

    const err = new Error(message);
    (err as any).status = data.status || res.status;
    (err as any).details = data.details || data;
    (err as any)._token_invalid = data._token_invalid;
    
    console.error(`xCloud Proxy Error [${res.status}]:`, {
      path,
      method,
      message,
      details: (err as any).details
    });
    
    throw err;
  }
  return data as T;
}

// Servers
export const listServers = (workspaceId: string, page: number = 1, force?: boolean) =>
  proxyRequest<XCloudListResponse<XCloudServer>>(workspaceId, 'servers', 'GET', undefined, force, page);

export const getServer = (workspaceId: string, serverUuid: string, force?: boolean) =>
  proxyRequest<XCloudDetailResponse<XCloudServer>>(workspaceId, `servers/${serverUuid}`, 'GET', undefined, force);

export const listServerSites = (workspaceId: string, serverUuid: string, page: number = 1, force?: boolean) =>
  proxyRequest<XCloudListResponse<XCloudSite>>(workspaceId, `servers/${serverUuid}/sites`, 'GET', undefined, force, page);

export const listServerDatabases = (workspaceId: string, serverUuid: string, force?: boolean) =>
  proxyRequest<XCloudListResponse<XCloudDatabase>>(workspaceId, `servers/${serverUuid}/databases`, 'GET', undefined, force);

export const listServerCronJobs = (workspaceId: string, serverUuid: string, force?: boolean) =>
  proxyRequest<XCloudListResponse<XCloudCronJob>>(workspaceId, `servers/${serverUuid}/cron-jobs`, 'GET', undefined, force);

export const getServerMonitoring = (workspaceId: string, serverUuid: string, force?: boolean) =>
  proxyRequest<XCloudDetailResponse<XCloudMonitoring>>(workspaceId, `servers/${serverUuid}/monitoring`, 'GET', undefined, force);

export const listServerTasks = (workspaceId: string, serverUuid: string, force?: boolean) =>
  proxyRequest<XCloudListResponse<XCloudTask>>(workspaceId, `servers/${serverUuid}/tasks`, 'GET', undefined, force);

export const getServerPhpVersions = (workspaceId: string, serverUuid: string, force?: boolean) =>
  proxyRequest<XCloudListResponse<XCloudPhpVersion>>(workspaceId, `servers/${serverUuid}/php-versions`, 'GET', undefined, force);

export const listServerSudoUsers = (workspaceId: string, serverUuid: string, force?: boolean) =>
  proxyRequest<XCloudListResponse<XCloudSudoUser>>(workspaceId, `servers/${serverUuid}/sudo-users`, 'GET', undefined, force);

export const rebootServer = (workspaceId: string, serverUuid: string) =>
  proxyRequest<{ message: string }>(workspaceId, `servers/${serverUuid}/reboot`, 'POST');

export const createSudoUser = (workspaceId: string, serverUuid: string, data: { username: string; password: string }) =>
  proxyRequest<XCloudDetailResponse<XCloudSudoUser>>(workspaceId, `servers/${serverUuid}/sudo-users`, 'POST', data);

export const deleteSudoUser = (workspaceId: string, serverUuid: string, sudoUserUuid: string) =>
  proxyRequest<{ message: string }>(workspaceId, `servers/${serverUuid}/sudo-users/${sudoUserUuid}`, 'DELETE');

export const createWordpressSite = (workspaceId: string, serverUuid: string, data: Record<string, unknown>) =>
  proxyRequest<XCloudDetailResponse<XCloudSite>>(workspaceId, `servers/${serverUuid}/sites/wordpress`, 'POST', data);

// Blueprints
export const listBlueprints = (workspaceId: string, force?: boolean) =>
  proxyRequest<XCloudListResponse<XCloudBlueprint>>(workspaceId, 'blueprints', 'GET', undefined, force);

// Sites
export const listSites = (workspaceId: string, page: number = 1, force?: boolean) =>
  proxyRequest<XCloudListResponse<XCloudSite>>(workspaceId, 'sites', 'GET', undefined, force, page);

export const getSite = (workspaceId: string, siteUuid: string, force?: boolean) =>
  proxyRequest<XCloudDetailResponse<XCloudSite>>(workspaceId, `sites/${siteUuid}`, 'GET', undefined, force);

export const getSiteStatus = (workspaceId: string, siteUuid: string, force?: boolean) =>
  proxyRequest<XCloudDetailResponse<XCloudSiteStatus>>(workspaceId, `sites/${siteUuid}/status`, 'GET', undefined, force);

export const getSiteSsl = (workspaceId: string, siteUuid: string, force?: boolean) =>
  proxyRequest<XCloudDetailResponse<XCloudSSL>>(workspaceId, `sites/${siteUuid}/ssl`, 'GET', undefined, force);

export const getSiteDomain = (workspaceId: string, siteUuid: string, force?: boolean) =>
  proxyRequest<XCloudDetailResponse<XCloudDomain>>(workspaceId, `sites/${siteUuid}/domain`, 'GET', undefined, force);

export const listSiteBackups = async (workspaceId: string, siteUuid: string, force?: boolean) => {
  try {
    return await proxyRequest<XCloudListResponse<XCloudBackup>>(workspaceId, `sites/${siteUuid}/backups`, 'GET', undefined, force);
  } catch (error: any) {
    if (error.status === 404) {
      // xCloud Beta API returns 404 when no backups are found for a site
      return { data: [] } as XCloudListResponse<XCloudBackup>;
    }
    if (error.status === 403) {
      throw new Error('Token lacks the required read:sites scope. Please generate a new token with proper permissions.');
    }
    throw error;
  }
};

export const getSiteMonitoring = (workspaceId: string, siteUuid: string, force?: boolean) =>
  proxyRequest<XCloudDetailResponse<XCloudMonitoring>>(workspaceId, `sites/${siteUuid}/monitoring`, 'GET', undefined, force);

export const listSiteEvents = (workspaceId: string, siteUuid: string, force?: boolean) =>
  proxyRequest<XCloudListResponse<{ uuid: string; description: string; created_at: string }>>(workspaceId, `sites/${siteUuid}/events`, 'GET', undefined, force);

export const listDeploymentLogs = (workspaceId: string, siteUuid: string, force?: boolean) =>
  proxyRequest<XCloudListResponse<XCloudDeploymentLog>>(workspaceId, `sites/${siteUuid}/deployment-logs`, 'GET', undefined, force);

export const getSiteGit = (workspaceId: string, siteUuid: string, force?: boolean) =>
  proxyRequest<XCloudDetailResponse<XCloudGit>>(workspaceId, `sites/${siteUuid}/git`, 'GET', undefined, force);

export const getSiteSsh = (workspaceId: string, siteUuid: string, force?: boolean) =>
  proxyRequest<XCloudDetailResponse<XCloudSsh>>(workspaceId, `sites/${siteUuid}/ssh`, 'GET', undefined, force);

export const updateSiteSsh = (workspaceId: string, siteUuid: string, data: Partial<XCloudSsh>) =>
  proxyRequest<XCloudDetailResponse<XCloudSsh>>(workspaceId, `sites/${siteUuid}/ssh`, 'PUT', data);

export const triggerSiteBackup = (workspaceId: string, siteUuid: string) =>
  proxyRequest<{ message: string }>(workspaceId, `sites/${siteUuid}/backup`, 'POST');

export const purgeSiteCache = (workspaceId: string, siteUuid: string) =>
  proxyRequest<{ message: string }>(workspaceId, `sites/${siteUuid}/cache/purge`, 'POST');

// Connect / Disconnect
export async function connectXCloud(workspaceId: string, token: string): Promise<{ email?: string }> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');

  const res = await fetch(`${SUPABASE_URL}/functions/v1/xcloud-connect`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
      Apikey: SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({ workspace_id: workspaceId, token }),
  });

  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to connect xCloud');
  return data;
}

export async function disconnectXCloud(workspaceId: string): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');

  const res = await fetch(`${SUPABASE_URL}/functions/v1/xcloud-disconnect`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
      Apikey: SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({ workspace_id: workspaceId }),
  });

  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to disconnect xCloud');
}
